import React, { PropsWithChildren, useEffect } from 'react';
import ReactModal from 'react-modal';
import cn from 'classnames';
import { ClassNameProps } from '@/Types';
import { ModalCloseIcon } from '@/Static';
import styles from './Modal.scss';
import { TopMenuIcons } from '@/Static/IconsRes';
import { useTourActions } from '@/Components/Tours/hooks/useTourActions';

export type DescriptionType = {
  id?: string;
  title?: string;
  titleId?: string;
  text?: string;
  textId?: string;
};

type Props = {
  isOpen?: boolean;
  tourKey?: string;
  needCloseIcon?: boolean;
  needRefDocsIcon?: boolean;
  shouldCloseOnEsc?: boolean;
  shouldCloseOnOverlayClick?: boolean;
  modalClassName?: string;
  modalId?: string;
  overlayClassName?: string;
  onClose: () => void;
  onDocsClick?: () => void;
  description?: DescriptionType;
} & ClassNameProps;

export function Modal({
  tourKey,
  isOpen = true,
  needCloseIcon = true,
  needRefDocsIcon = true,
  shouldCloseOnEsc = true,
  shouldCloseOnOverlayClick = false,
  className,
  modalClassName,
  modalId,
  overlayClassName,
  onClose,
  onDocsClick,
  children,
  description,
}: PropsWithChildren<Props>) {
  const { runTour } = useTourActions();

  useEffect(() => {
    if (isOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [isOpen]);

  const onClickDocs = () => {
    if (onDocsClick) {
      onDocsClick?.();
    } else if (tourKey) {
      runTour(tourKey);
    }
  };

  return (
    <ReactModal
      isOpen={isOpen}
      className={cn(styles.modal, modalClassName)}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      shouldCloseOnEsc={shouldCloseOnEsc}
      onRequestClose={onClose}
      overlayClassName={overlayClassName}
      id={modalId}
    >
      <div className={styles.modalButtons}>
        {needRefDocsIcon && (
          <img src={TopMenuIcons.Learn} alt="doc" className={styles.docsButton} onClick={onClickDocs} />
        )}
        {needCloseIcon && <ModalCloseIcon size={'small'} onClick={onClose} />}
      </div>

      <div className={cn(styles.modalContent, className)}>
        {description && (
          <div className={styles.modalDescription} id={description.id}>
            {description.title && <h3 id={description?.titleId}>{description?.title}</h3>}
            {description.text && <p id={description?.textId}>{description?.text}</p>}
          </div>
        )}

        {children}
      </div>
    </ReactModal>
  );
}
